/**
 * Candidate-facing assessment runner.
 *
 * Public route (token-authed): /assess/:token
 * Renders a full-screen test taker with:
 *   • countdown timer
 *   • question navigation
 *   • integrity hooks (tab-switch / fullscreen-exit / copy-paste / right-click / devtools)
 *   • Monaco-ish lightweight code editor (textarea fallback) for coding qs
 *   • auto-submit on expiry
 *
 * No SkillConnect login required — the URL token IS the auth.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Clock, ChevronLeft, ChevronRight, Flag, Send, AlertTriangle, Maximize2,
  CheckCircle2, ShieldAlert, Loader2, Sparkles, Play, Camera, CameraOff,
} from "lucide-react";
import usePageTitle from "../hooks/usePageTitle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ baseURL: API, withCredentials: false });

function useCountdown(endTime) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!endTime) return null;
  return Math.max(0, Math.floor((endTime - now) / 1000));
}

function fmtTime(sec) {
  if (sec == null) return "—";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return (h ? `${String(h).padStart(2, "0")}:` : "") + `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function AssessmentRunner() {
  usePageTitle("Take Assessment");
  const { token } = useParams();
  const [boot, setBoot] = useState(null);     // {invitation, template, questions, session}
  const [phase, setPhase] = useState("loading"); // loading|consent|running|submitted|expired|err
  const [err, setErr] = useState("");
  const [answers, setAnswers] = useState({}); // qId → answer obj
  const [idx, setIdx] = useState(0);
  const [flagged, setFlagged] = useState({}); // qId → bool
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const remaining = useCountdown(endTime);
  const [runningCode, setRunningCode] = useState(false);
  const [codeResult, setCodeResult] = useState(null); // {question_id, ...}
  const [proctorStream, setProctorStream] = useState(null);
  const [proctorError, setProctorError] = useState("");
  const proctorVideoRef = useRef(null);
  const proctorCanvasRef = useRef(null);
  const snapshotsTakenRef = useRef(0);

  const runCode = useCallback(async (q) => {
    if (runningCode) return;
    const code = answers[q.id]?.code_answer ?? (q.starter_code || "");
    if (!code.trim()) { setCodeResult({ question_id: q.id, stdout: "", stderr: "Nothing to run — type some code first.", exit_code: null, elapsed_ms: 0 }); return; }
    setRunningCode(true); setCodeResult(null);
    try {
      const r = await ax.post(`/assess/runner/${token}/code/run`, {
        language: q.language || "python",
        code,
      });
      setCodeResult({ question_id: q.id, ...r.data });
    } catch (e) {
      setCodeResult({
        question_id: q.id, stdout: "", exit_code: null, elapsed_ms: 0,
        stderr: e?.response?.data?.detail || e.message,
      });
    } finally { setRunningCode(false); }
  }, [answers, runningCode, token]);

  // Bootstrap
  useEffect(() => {
    (async () => {
      try {
        const r = await ax.get(`/assess/runner/${token}`);
        setBoot(r.data);
        if (r.data.invitation.status === "completed") setPhase("submitted");
        else setPhase("consent");
        // Hydrate previous answers if any
        const prev = r.data.session?.answers || [];
        const map = {}; const fmap = {};
        for (const a of prev) {
          map[a.question_id] = a;
          if (a.flagged) fmap[a.question_id] = true;
        }
        setAnswers(map); setFlagged(fmap);
      } catch (e) {
        setErr(e?.response?.data?.detail || e.message);
        setPhase(e?.response?.status === 410 ? "expired" : "err");
      }
    })();
  }, [token]);

  // ─── INTEGRITY HOOKS ─────────────────────────────────────────────
  const signal = useCallback(async (kind, meta = {}) => {
    try { await ax.post(`/assess/runner/${token}/signal`, { kind, meta }); }
    catch (e) {
      // best-effort beacon — never block the candidate's UX on signal failures
      if (process.env.NODE_ENV !== "production") console.debug("signal failed:", kind, e?.message);
    }
  }, [token]);

  useEffect(() => {
    if (phase !== "running") return;
    const onBlur = () => signal("tab_blur");
    const onFocus = () => signal("tab_focus");
    const onFs = () => {
      signal(document.fullscreenElement ? "fullscreen_enter" : "fullscreen_exit");
    };
    const onCopy = (e) => { signal("copy_attempt"); e.preventDefault(); };
    const onPaste = (e) => { signal("paste_attempt"); /* allow paste only inside code editor (textarea) */ if (!(e.target instanceof HTMLTextAreaElement)) e.preventDefault(); };
    const onCtx = (e) => { e.preventDefault(); signal("right_click"); };
    const onKey = (e) => {
      // F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+U
      const k = e.key.toLowerCase();
      if (e.key === "F12" || (e.ctrlKey && e.shiftKey && (k === "i" || k === "j" || k === "c")) ||
          (e.ctrlKey && k === "u")) {
        e.preventDefault(); signal("key_devtools", { key: e.key });
      }
    };
    const onLeave = (e) => { if (e.clientY < 0 || e.clientX < 0) signal("mouse_leave"); };
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    document.addEventListener("fullscreenchange", onFs);
    document.addEventListener("copy", onCopy);
    document.addEventListener("paste", onPaste);
    document.addEventListener("contextmenu", onCtx);
    document.addEventListener("keydown", onKey);
    document.addEventListener("mouseleave", onLeave);
    return () => {
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("fullscreenchange", onFs);
      document.removeEventListener("copy", onCopy);
      document.removeEventListener("paste", onPaste);
      document.removeEventListener("contextmenu", onCtx);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mouseleave", onLeave);
    };
  }, [phase, signal]);

  const start = async () => {
    setErr("");
    try {
      // Request camera access (proctoring) — best-effort, non-blocking
      let stream = null;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { width: 320, height: 240 }, audio: false });
        setProctorStream(stream);
      } catch (cam) {
        setProctorError(cam?.message || "Camera permission denied");
        if (process.env.NODE_ENV !== "production") console.debug("camera permission:", cam?.message);
      }
      // Try to enter fullscreen
      try { await document.documentElement.requestFullscreen(); }
      catch (fe) {
        if (process.env.NODE_ENV !== "production") console.debug("fullscreen denied:", fe?.message);
      }
      const r = await ax.post(`/assess/runner/${token}/start`);
      const startedAt = new Date(r.data.started_at).getTime();
      const minutes = boot.template.total_duration_min || 60;
      setEndTime(startedAt + minutes * 60_000);
      setPhase("running");
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
  };

  // Attach the camera stream to the hidden <video> element so we can grab frames
  useEffect(() => {
    if (proctorStream && proctorVideoRef.current) {
      proctorVideoRef.current.srcObject = proctorStream;
    }
    return () => {
      if (proctorStream) {
        proctorStream.getTracks().forEach((t) => { try { t.stop(); } catch { /* ignore */ } });
      }
    };
  }, [proctorStream]);

  // Periodic webcam snapshot upload every 30s while running
  useEffect(() => {
    if (phase !== "running" || !proctorStream) return undefined;
    const capture = async () => {
      try {
        const video = proctorVideoRef.current;
        const canvas = proctorCanvasRef.current;
        if (!video || !canvas || video.readyState < 2) return;
        canvas.width = 320; canvas.height = 240;
        canvas.getContext("2d").drawImage(video, 0, 0, 320, 240);
        const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.6));
        if (!blob) return;
        const fd = new FormData();
        fd.append("file", blob, `snap_${Date.now()}.jpg`);
        await ax.post(`/assess/runner/${token}/snapshot`, fd);
        snapshotsTakenRef.current += 1;
      } catch (e) {
        if (process.env.NODE_ENV !== "production") console.debug("snapshot upload failed:", e?.message);
      }
    };
    // First snapshot 5s after start, then every 30s
    const first = setTimeout(capture, 5_000);
    const periodic = setInterval(capture, 30_000);
    return () => { clearTimeout(first); clearInterval(periodic); };
  }, [phase, proctorStream, token]);

  const saveAnswer = useCallback(async (q, patch) => {
    const cur = answers[q.id] || { question_id: q.id, time_taken_sec: 0 };
    const updated = { ...cur, ...patch };
    setAnswers((a) => ({ ...a, [q.id]: updated }));
    try { await ax.post(`/assess/runner/${token}/answer`, { ...updated, flagged: !!flagged[q.id] }); }
    catch (e) {
      // Progressive save — retried on submit. Don't bother the candidate now.
      if (process.env.NODE_ENV !== "production") console.debug("answer save failed:", e?.message);
    }
  }, [answers, flagged, token]);

  const submit = useCallback(async (auto = false) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const r = await ax.post(`/assess/runner/${token}/submit`);
      setResult({ ...r.data, auto });
      setPhase("submitted");
      try { document.exitFullscreen?.(); }
      catch (e) {
        if (process.env.NODE_ENV !== "production") console.debug("exit-fullscreen failed:", e?.message);
      }
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setSubmitting(false); }
  }, [token, submitting]);

  // Auto-submit when timer hits 0
  useEffect(() => {
    if (phase === "running" && remaining === 0) submit(true);
  }, [phase, remaining, submit]);

  // ─── Render ─────────────────────────────────────────────────────
  if (phase === "loading") return <Center><Loader2 className="animate-spin" /> Loading…</Center>;
  if (phase === "expired") return <ErrorScreen icon={Clock} title="This link has expired" body={err} />;
  if (phase === "err")     return <ErrorScreen icon={AlertTriangle} title="Couldn’t load assessment" body={err} />;

  if (phase === "submitted") return <SubmittedScreen boot={boot} result={result} />;

  if (phase === "consent") {
    return <ConsentScreen boot={boot} onStart={start} err={err} />;
  }

  // running
  const qs = boot.questions || [];
  const q = qs[idx];
  if (!q) return <Center>No questions in this assessment.</Center>;
  const totalAnswered = Object.values(answers).filter((a) => a.selected_index != null || a.boolean_answer != null || (a.code_answer || "").trim()).length;

  return (
    <div className="min-h-screen" style={{ background: "#0B1220", color: "#E2E8F0", userSelect: "none" }}
         onCopy={(e) => e.preventDefault()} data-testid="assessment-runner">
      {/* Hidden video element receiving the webcam stream + canvas used to grab frames */}
      <video ref={proctorVideoRef} autoPlay playsInline muted style={{ position: "fixed", bottom: 12, right: 12, width: 96, height: 72, borderRadius: 8, border: "2px solid #6366F1", zIndex: 30, background: "#000" }} data-testid="proctor-pip" />
      <canvas ref={proctorCanvasRef} style={{ display: "none" }} />
      {proctorError && (
        <div className="fixed bottom-2 right-32 z-30 inline-flex items-center gap-1.5 text-[10px] font-bold px-2 py-1 rounded bg-amber-900/80 text-amber-200" data-testid="proctor-err">
          <CameraOff size={11} /> Camera off
        </div>
      )}
      {/* Top bar */}
      <header className="sticky top-0 z-20 border-b" style={{ background: "#0F172A", borderColor: "#1E293B" }}>
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center gap-4">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-slate-400">{boot.template.title}</div>
            <div className="text-sm font-semibold truncate">{boot.invitation.candidate_name || boot.invitation.candidate_email}</div>
          </div>
          <div className="ml-auto inline-flex items-center gap-2 px-3 py-1.5 rounded-md tabular-nums"
            style={{ background: remaining != null && remaining < 300 ? "#7F1D1D" : "#1E293B", color: remaining != null && remaining < 300 ? "#FECACA" : "#E2E8F0" }}
            data-testid="timer">
            <Clock size={14} /> {fmtTime(remaining)}
          </div>
          <button className="text-xs font-bold px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white inline-flex items-center gap-1.5"
            disabled={submitting} onClick={() => submit(false)} data-testid="submit-btn">
            {submitting ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Submit
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-6 grid grid-cols-1 md:grid-cols-[1fr_220px] gap-6">
        {/* Question pane */}
        <main>
          <div className="text-[11px] uppercase tracking-wider text-slate-400">Question {idx + 1} of {qs.length}</div>
          <h2 className="font-display font-bold text-xl mt-2 leading-snug" data-testid="q-text">{q.text}</h2>

          <div className="mt-5 space-y-2.5" data-testid="q-input">
            {q.type === "mcq" && (q.options || []).map((opt, i) => {
              const checked = answers[q.id]?.selected_index === i;
              return (
                <label key={`${q.id}-opt-${i}-${opt.slice(0, 32)}`} className="block cursor-pointer">
                  <div className={`p-3 rounded-lg border-2 transition-all ${checked ? "border-sky-400 bg-sky-950/30" : "border-slate-700 hover:border-slate-500"}`}>
                    <input type="radio" name={`q-${q.id}`} className="mr-3" checked={!!checked}
                      onChange={() => saveAnswer(q, { selected_index: i })}
                      data-testid={`q-opt-${i}`} />
                    {opt}
                  </div>
                </label>
              );
            })}

            {q.type === "boolean" && [true, false].map((val) => {
              const checked = answers[q.id]?.boolean_answer === val;
              return (
                <label key={String(val)} className="block cursor-pointer">
                  <div className={`p-3 rounded-lg border-2 transition-all ${checked ? "border-sky-400 bg-sky-950/30" : "border-slate-700 hover:border-slate-500"}`}>
                    <input type="radio" name={`q-${q.id}`} className="mr-3" checked={!!checked}
                      onChange={() => saveAnswer(q, { boolean_answer: val })} />
                    {val ? "True" : "False"}
                  </div>
                </label>
              );
            })}

            {q.type === "coding" && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] text-slate-400">
                    Language: {q.language || "python"} · Paste-only is allowed inside this editor.
                  </div>
                  <button type="button"
                    className="text-[11px] font-bold px-2.5 py-1 rounded bg-sky-700 hover:bg-sky-600 text-white inline-flex items-center gap-1 disabled:opacity-50"
                    onClick={() => runCode(q)}
                    disabled={runningCode}
                    data-testid="run-code-btn">
                    {runningCode ? <Loader2 size={11} className="animate-spin" /> : <Play size={11} />} Run
                  </button>
                </div>
                <textarea
                  className="w-full h-56 font-mono text-sm p-3 rounded-lg border-2 bg-slate-950 text-emerald-200 border-slate-700 focus:border-sky-400 outline-none"
                  spellCheck={false}
                  value={answers[q.id]?.code_answer ?? (q.starter_code || "")}
                  onChange={(e) => saveAnswer(q, { code_answer: e.target.value })}
                  data-testid="q-code"
                />
                {codeResult && codeResult.question_id === q.id && (
                  <div className="mt-3 rounded-lg border border-slate-700 bg-slate-950 p-3" data-testid="code-output">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 inline-flex items-center gap-2">
                      Output
                      <span className="font-mono">exit={codeResult.exit_code} · {codeResult.elapsed_ms}ms</span>
                      {codeResult.timed_out && <span className="text-rose-400">· timed out</span>}
                    </div>
                    {codeResult.stdout && <pre className="text-xs text-emerald-300 mt-2 whitespace-pre-wrap">{codeResult.stdout}</pre>}
                    {codeResult.stderr && <pre className="text-xs text-rose-400 mt-2 whitespace-pre-wrap">{codeResult.stderr}</pre>}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="mt-6 flex items-center gap-3">
            <button className="text-xs font-bold px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 inline-flex items-center gap-1.5 disabled:opacity-50"
              disabled={idx === 0} onClick={() => setIdx((i) => Math.max(0, i - 1))} data-testid="prev-btn">
              <ChevronLeft size={12} /> Previous
            </button>
            <button className="text-xs font-bold px-3 py-2 rounded-md inline-flex items-center gap-1.5"
              style={{ background: flagged[q.id] ? "#9F1239" : "#334155" }}
              onClick={() => setFlagged((f) => ({ ...f, [q.id]: !f[q.id] }))} data-testid="flag-btn">
              <Flag size={12} /> {flagged[q.id] ? "Unflag" : "Flag for review"}
            </button>
            <button className="ml-auto text-xs font-bold px-3 py-2 rounded-md bg-sky-600 hover:bg-sky-500 inline-flex items-center gap-1.5 disabled:opacity-50"
              disabled={idx === qs.length - 1} onClick={() => setIdx((i) => Math.min(qs.length - 1, i + 1))} data-testid="next-btn">
              Next <ChevronRight size={12} />
            </button>
          </div>
        </main>

        {/* Sidebar navigator */}
        <aside className="rounded-xl p-4" style={{ background: "#0F172A", border: "1px solid #1E293B" }}>
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Progress</div>
          <div className="font-display font-bold text-2xl mt-1">{totalAnswered}/{qs.length}</div>
          <div className="text-[11px] text-slate-400 mt-1">answered</div>
          <div className="mt-4 grid grid-cols-5 gap-1.5" data-testid="q-grid">
            {qs.map((qq, i) => {
              const a = answers[qq.id];
              const isAnswered = a && (a.selected_index != null || a.boolean_answer != null || (a.code_answer || "").trim());
              const isFlagged = flagged[qq.id];
              return (
                <button key={qq.id} onClick={() => setIdx(i)} data-testid={`q-jump-${i}`}
                  className={`text-[10px] font-bold tabular-nums rounded h-7 transition-all border-2 ${
                    i === idx ? "border-sky-300 ring-1 ring-sky-300" : "border-transparent"
                  } ${
                    isFlagged ? "bg-rose-700 text-white" :
                    isAnswered ? "bg-emerald-700 text-white" :
                    "bg-slate-700 text-slate-200 hover:bg-slate-600"
                  }`}>
                  {i + 1}
                </button>
              );
            })}
          </div>
          <div className="mt-4 space-y-1 text-[10px] text-slate-400">
            <div><span className="inline-block w-2 h-2 bg-emerald-700 mr-1.5 rounded-sm" /> Answered</div>
            <div><span className="inline-block w-2 h-2 bg-rose-700 mr-1.5 rounded-sm" /> Flagged</div>
            <div><span className="inline-block w-2 h-2 bg-slate-700 mr-1.5 rounded-sm" /> Unanswered</div>
          </div>
        </aside>
      </div>
    </div>
  );
}

// ─── helpers ─────────────────────────────────────────────────────
function Center({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center text-slate-200" style={{ background: "#0B1220" }}>
      <div className="inline-flex items-center gap-2">{children}</div>
    </div>
  );
}

function ErrorScreen({ icon: Icon, title, body }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "#0B1220" }}>
      <div className="max-w-md text-center text-slate-200" data-testid="runner-error">
        <Icon size={36} className="mx-auto text-rose-400" />
        <h1 className="font-display font-bold text-2xl mt-3">{title}</h1>
        <p className="text-sm text-slate-400 mt-2">{body}</p>
      </div>
    </div>
  );
}

function ConsentScreen({ boot, onStart, err }) {
  const t = boot.template;
  const inv = boot.invitation;
  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-10" style={{ background: "linear-gradient(135deg, #0B1220 0%, #0F172A 50%, #1E1B4B 100%)", color: "#E2E8F0" }}>
      <div className="max-w-2xl bg-slate-900/80 backdrop-blur rounded-2xl border border-slate-700 p-8 shadow-2xl" data-testid="consent-screen">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-bold" style={{ color: "#7DD3FC" }}>
          <Sparkles size={14} /> Assessment ready
        </div>
        <h1 className="font-display font-black text-3xl mt-3 leading-tight">{t.title}</h1>
        {t.description && <p className="text-slate-300 mt-3 leading-relaxed">{t.description}</p>}

        <div className="grid grid-cols-2 gap-3 mt-6">
          <Tile label="Candidate"   v={inv.candidate_name || inv.candidate_email} />
          <Tile label="Duration"    v={`${t.total_duration_min} minutes`} />
          <Tile label="Questions"   v={(boot.questions || []).length} />
          <Tile label="Passing"     v={`${t.passing_score}%`} />
        </div>

        <div className="mt-6 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-amber-200">
          <div className="font-bold inline-flex items-center gap-1.5"><ShieldAlert size={14} /> Integrity policy</div>
          <ul className="text-xs mt-2 space-y-1 list-disc pl-5 text-amber-100/90">
            <li>The test runs in fullscreen and will warn if you exit.</li>
            <li>Switching tabs, copying, pasting, opening DevTools or leaving the window is recorded.</li>
            <li>Right-click is disabled. Each event lowers your integrity score.</li>
            <li>The test auto-submits when the timer reaches 0.</li>
          </ul>
        </div>

        <div className="mt-4 rounded-xl border border-indigo-500/30 bg-indigo-500/5 p-4 text-indigo-100">
          <div className="font-bold inline-flex items-center gap-1.5"><Camera size={14} /> Webcam check-ins</div>
          <p className="text-xs mt-2 text-indigo-100/90">
            We&apos;ll take a small snapshot from your webcam every 30 seconds while the test runs (sent over a secure channel and visible only to the hiring team). You can decline — the test will continue without snapshots, but your integrity score will reflect it.
          </p>
        </div>

        {err && <div className="text-rose-400 text-sm mt-4">{err}</div>}

        <button
          onClick={onStart}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold text-white shadow-xl"
          style={{ background: "linear-gradient(135deg, #38BDF8, #6366F1)" }}
          data-testid="start-btn"
        >
          <Maximize2 size={16} /> Start in fullscreen
        </button>
      </div>
    </div>
  );
}

function Tile({ label, v }) {
  return (
    <div className="rounded-lg bg-slate-800/60 border border-slate-700 p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className="font-semibold mt-1 break-words">{v}</div>
    </div>
  );
}

function SubmittedScreen({ boot, result }) {
  const score = result?.score_pct ?? boot?.session?.score_pct;
  const integrity = result?.integrity_score ?? boot?.session?.integrity_score;
  const passed = score != null && score >= (boot?.template?.passing_score ?? 60);
  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-10" style={{ background: "#0B1220", color: "#E2E8F0" }}>
      <div className="max-w-md text-center bg-slate-900/80 rounded-2xl border border-slate-700 p-8" data-testid="submitted-screen">
        <CheckCircle2 size={48} className="mx-auto text-emerald-400" />
        <h1 className="font-display font-bold text-2xl mt-3">Submitted</h1>
        <p className="text-slate-400 text-sm mt-2">Thanks for completing <b>{boot?.template?.title}</b>. Hiring team will review and reach out.</p>
        {score != null && (
          <div className="mt-5 grid grid-cols-2 gap-3">
            <Tile label="Score" v={`${score}% · ${passed ? "✓ pass" : "below cut-off"}`} />
            <Tile label="Integrity" v={integrity != null ? `${integrity}/100` : "—"} />
          </div>
        )}
        {result?.auto && <div className="text-amber-300 text-xs mt-4">Auto-submitted on timer.</div>}
      </div>
    </div>
  );
}
